// components/finance/TdsGlSection.tsx
import { SimpleGrid, HStack, IconButton, useToast } from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { addNA } from "App/BsesPoc/components/common/function";
import CustomInput from "components/common/customInput/CustomInput";
import { useEffect, useState, useCallback, useRef } from "react";
import { appRequest } from "routes";

interface TdsGlSectionProps {
  formData: any;
  setFormData: (updater: (prev: any) => any) => void;
  companyCode: any;
  workflowProcess: any;
  isDisabled?: boolean;
}

export default function TdsGlSection({
  formData,
  setFormData,
  companyCode,
  workflowProcess,
  isDisabled = false
}: TdsGlSectionProps) {
  const [profitCenterOptions, setProfitCenterOptions] = useState<any[]>([]);
  const [businessAreaOptions, setBusinessAreaOptions] = useState<any[]>([]);
  const [glAccountOptions, setGlAccountOptions] = useState<any[]>([]);
  const [internalOrderOptions, setInternalOrderOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState({
    profitCenter: false,
    businessArea: false,
    glAccount: false,
    internalOrder: false,
    plantCode: false,
    businessPlace: false,
  });

  const [plantCodeOptions, setPlantCodeOptions] = useState<any[]>([]);
  const [businessPlaceOptions, setBusinessPlaceOptions] = useState<any[]>([]);

  // Refs to prevent redundant fetching
  const lastFetchedProfitCompanyCode = useRef<string | null>(null);
  const lastFetchedBusinessAreaKey = useRef<string | null>(null);
  const lastFetchedGlCompanyCode = useRef<string | null>(null);
  const lastFetchedInternalOrderKey = useRef<string | null>(null);
  const lastFetchedPlantCompanyCode = useRef<string | null>(null);
  const lastFetchedBusinessPlaceKey = useRef<string | null>(null);

  const toast = useToast();

  // 1. Fetch Profit Centers
  const fetchProfitCenters = useCallback(async (isManual = false) => {
    if (!companyCode?.value) {
      setProfitCenterOptions([]);
      return;
    }

    if (!isManual && lastFetchedProfitCompanyCode.current === companyCode.value) return;

    setLoading((prev) => ({ ...prev, profitCenter: true }));
    try {
      const payload = {
        type: "getProfitCenter",
        companyCode: companyCode.value,
        ...workflowProcess,
      };
      const res: any = await appRequest("user", "masterFinancialApi", payload);
      if (res?.status === "success" && Array.isArray(res.data)) {
        const opts = res.data.map((pc: any) => ({
          label: pc.label || pc.profitCenter || pc.value,
          value: pc.profitCenter || pc.value,
        }));
        setProfitCenterOptions(opts);
        lastFetchedProfitCompanyCode.current = companyCode.value;
        if (isManual) {
          toast({
            title: "Profit Centers Refreshed",
            status: "success",
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (err: any) {
      console.error("Failed to load profit centers for TDS GL", err);
      setProfitCenterOptions([]);
      if (isManual) {
        toast({
          title: "Failed to load Profit Centers",
          description: err?.message || "Please try again later.",
          status: "error",
          duration: 4000,
        });
      }
    } finally {
      setLoading((prev) => ({ ...prev, profitCenter: false }));
    }
  }, [companyCode?.value, workflowProcess, toast]);

  useEffect(() => {
    fetchProfitCenters();
  }, [fetchProfitCenters]);

  // 2. Fetch Business Area when Profit Center selected
  const fetchBusinessArea = useCallback(async (isManual = false) => {
    if (!formData.glProfitCenter?.value || !companyCode?.value) {
      setBusinessAreaOptions([]);
      return;
    }

    const cacheKey = `${companyCode.value}-${formData.glProfitCenter.value}`;
    if (!isManual && lastFetchedBusinessAreaKey.current === cacheKey) return;

    setLoading((prev) => ({ ...prev, businessArea: true }));
    try {
      const payload = {
        type: "getBusinessArea",
        companyCode: companyCode.value,
        profitCenter: formData.glProfitCenter.value,
        costCenter: "",
        ...workflowProcess,
      };
      const res: any = await appRequest("user", "masterFinancialApi", payload);
      if (res?.status === "success" && Array.isArray(res.data)) {
        const opts = res.data.map((ba: string) => ({
          label: ba,
          value: ba,
        }));
        setBusinessAreaOptions(opts);
        lastFetchedBusinessAreaKey.current = cacheKey;
        if (isManual) {
          toast({
            title: "Business Areas Refreshed",
            status: "success",
            duration: 2000,
            isClosable: true,
          });
        }
      } else {
        setBusinessAreaOptions([]);
      }
    } catch (err: any) {
      console.error("Failed to load business areas", err);
      setBusinessAreaOptions([]);
      if (isManual) {
        toast({
          title: "Failed to load Business Areas",
          description: err?.message || "Please try again later.",
          status: "error",
          duration: 4000,
        });
      }
    } finally {
      setLoading((prev) => ({ ...prev, businessArea: false }));
    }
  }, [companyCode?.value, formData.glProfitCenter, workflowProcess, toast]);

  useEffect(() => {
    fetchBusinessArea();
  }, [fetchBusinessArea]);

  // 3. Fetch GL Accounts
  const fetchGlAccounts = useCallback(async (isManual = false) => {
    if (!companyCode?.value) {
      setGlAccountOptions([]);
      return;
    }

    if (!isManual && lastFetchedGlCompanyCode.current === companyCode.value) return;

    setLoading((prev) => ({ ...prev, glAccount: true }));
    try {
      const payload = {
        type: "getGlAccount",
        companyCode: companyCode.value,
        ...workflowProcess,
      };
      const res: any = await appRequest("user", "masterFinancialApi", payload);
      if (res?.status === "success" && Array.isArray(res.data)) {
        const opts = res.data.map((item: any) => ({
          label: item.label || `${item.glAccount} - ${item.description || ""}`.trim(),
          value: item.glAccount,
        }));
        setGlAccountOptions(opts);
        lastFetchedGlCompanyCode.current = companyCode.value;
        if (isManual) {
          toast({
            title: "GL Accounts Refreshed",
            status: "success",
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (err: any) {
      console.error("Failed to load GL accounts", err);
      if (isManual) {
        toast({
          title: "Failed to load GL Accounts",
          description: err?.message || "Please try again later.",
          status: "error",
          duration: 4000,
        });
      }
    } finally {
      setLoading((prev) => ({ ...prev, glAccount: false }));
    }
  }, [companyCode?.value, workflowProcess, toast]);

  useEffect(() => {
    fetchGlAccounts();
  }, [fetchGlAccounts]);

  // 4. Fetch Internal Orders
  const fetchInternalOrders = useCallback(async (isManual = false) => {
    if (!formData.glAccount?.value || !companyCode?.value) {
      setInternalOrderOptions([]);
      return;
    }

    const cacheKey = `${companyCode.value}-${formData.glAccount.value}-${formData.glProfitCenter?.value || "NA"}`;
    if (!isManual && lastFetchedInternalOrderKey.current === cacheKey) return;

    setLoading((prev) => ({ ...prev, internalOrder: true }));
    try {
      const payload = {
        type: "getInternalOrder",
        companyCode: companyCode.value,
        glAccount: formData.glAccount.value,
        profitCenter: formData.glProfitCenter?.value || null,
        ...workflowProcess,
      };
      const res: any = await appRequest("user", "masterFinancialApi", payload);
      if (res?.status === "success" && Array.isArray(res.data)) {
        const opts = res.data.map((io: any) => ({
          label: typeof io === "string" ? io : io.label || io,
          value: typeof io === "string" ? io : io.value || io,
        }));
        setInternalOrderOptions(opts);
        lastFetchedInternalOrderKey.current = cacheKey;
        if (isManual) {
          toast({
            title: "Internal Orders Refreshed",
            status: "success",
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (err: any) {
      console.error("Failed to load internal orders", err);
      setInternalOrderOptions([]);
      if (isManual) {
        toast({
          title: "Failed to load Internal Orders",
          description: err?.message || "Please try again later.",
          status: "error",
          duration: 4000,
        });
      }
    } finally {
      setLoading((prev) => ({ ...prev, internalOrder: false }));
    }
  }, [companyCode?.value, formData.glAccount, formData.glProfitCenter, workflowProcess, toast]);

  useEffect(() => {
    fetchInternalOrders();
  }, [fetchInternalOrders]);

  // 5. Fetch Plant Codes
  const fetchPlantCodes = useCallback(async (isManual = false) => {
    if (!companyCode?.value) {
      setPlantCodeOptions([]);
      return;
    }

    if (!isManual && lastFetchedPlantCompanyCode.current === companyCode.value) return;

    setLoading((prev) => ({ ...prev, plantCode: true }));
    try {
      const payload = {
        type: "getPlantCode",
        companyCode: companyCode.value,
        ...workflowProcess,
      };
      const res: any = await appRequest("user", "masterFinancialApi", payload);
      if (res?.status === "success" && Array.isArray(res.data)) {
        const opts = res.data.map((pc: any) => ({
          label: pc.label || pc.value || pc,
          value: pc.value || pc,
        }));
        setPlantCodeOptions(opts);
        lastFetchedPlantCompanyCode.current = companyCode.value;
        if (isManual) {
          toast({
            title: "Plant Codes Refreshed",
            status: "success",
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (err: any) {
      console.error("Failed to load plant codes", err);
      setPlantCodeOptions([]);
      if (isManual) {
        toast({
          title: "Failed to load Plant Codes",
          description: err?.message || "Please try again later.",
          status: "error",
          duration: 4000,
        });
      }
    } finally {
      setLoading((prev) => ({ ...prev, plantCode: false }));
    }
  }, [companyCode?.value, workflowProcess, toast]);

  useEffect(() => {
    fetchPlantCodes();
  }, [fetchPlantCodes]);

  // 6. Fetch Business Place when Plant Code selected
  const fetchBusinessPlace = useCallback(async (isManual = false) => {
    if (!formData.glPlantCode?.value || !companyCode?.value) {
      setBusinessPlaceOptions([]);
      return;
    }

    const cacheKey = `${companyCode.value}-${formData.glPlantCode.value}`;
    if (!isManual && lastFetchedBusinessPlaceKey.current === cacheKey) return;

    setLoading((prev) => ({ ...prev, businessPlace: true }));
    try {
      const payload = {
        type: "getBusinessPlace",
        companyCode: companyCode.value,
        plantCode: formData.glPlantCode.value,
        ...workflowProcess,
      };
      const res: any = await appRequest("user", "masterFinancialApi", payload);
      if (res?.status === "success" && Array.isArray(res.data)) {
        const opts = res.data.map((bp: any) => ({
          label: bp.label || bp.value || bp,
          value: bp.value || bp,
        }));
        setBusinessPlaceOptions(opts);
        lastFetchedBusinessPlaceKey.current = cacheKey;
        if (isManual) {
          toast({
            title: "Business Places Refreshed",
            status: "success",
            duration: 2000,
            isClosable: true,
          });
        }
      } else {
        setBusinessPlaceOptions([]);
      }
    } catch (err: any) {
      console.error("Failed to load business places", err);
      setBusinessPlaceOptions([]);
      if (isManual) {
        toast({
          title: "Failed to load Business Places",
          description: err?.message || "Please try again later.",
          status: "error",
          duration: 4000,
        });
      }
    } finally {
      setLoading((prev) => ({ ...prev, businessPlace: false }));
    }
  }, [companyCode?.value, formData.glPlantCode, workflowProcess, toast]);

  useEffect(() => {
    fetchBusinessPlace();
  }, [fetchBusinessPlace]);



  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 2 }} spacing={4}>

      <HStack alignItems="flex-end" spacing={1}>
        <CustomInput
          name="glPlantCode"
          label="Plant Code"
          type="select"
          isBoxSelect
          options={addNA(plantCodeOptions)}
          value={formData.glPlantCode}
          onChange={(selected: any) => {
            setFormData((prev: any) => ({
              ...prev,
              glPlantCode: selected,
              glBusinessPlace: null,
            }));
          }}
          // disabled={isDisabled}
          placeholder={loading.plantCode ? "Loading..." : "Select Plant Code"}
          isRequired={true}
          disabled={isDisabled}
        />
        <IconButton
          aria-label="Refresh Plant Code"
          icon={<RepeatIcon />}
          size="sm"
          onClick={() => fetchPlantCodes(true)}
          isLoading={loading.plantCode}
          isDisabled={isDisabled || !companyCode?.value}
          mb="2px"
        />
      </HStack>

      <HStack alignItems="flex-end" spacing={1}>
        <CustomInput
          name="glBusinessPlace"
          label="Business Place"
          type="select"
          isBoxSelect
          options={addNA(businessPlaceOptions)}
          value={formData.glBusinessPlace}
          onChange={(selected: any) =>
            setFormData((prev: any) => ({ ...prev, glBusinessPlace: selected }))
          }
          // disabled={isDisabled || !formData.glPlantCode}
          placeholder={loading.businessPlace ? "Loading..." : "Select Business Place"}
          isRequired={true}
          disabled={isDisabled}
        />
        <IconButton
          aria-label="Refresh Business Place"
          icon={<RepeatIcon />}
          size="sm"
          onClick={() => fetchBusinessPlace(true)}
          isLoading={loading.businessPlace}
          isDisabled={isDisabled || !formData.glPlantCode}
          mb="2px"
        />
      </HStack>

      <HStack alignItems="flex-end" spacing={1}>
        <CustomInput
          name="glProfitCenter"
          label="Profit Center"
          type="select"
          isBoxSelect
          options={addNA(profitCenterOptions)}
          value={formData.glProfitCenter}
          onChange={(selected: any) => {
            setFormData((prev: any) => ({
              ...prev,
              glProfitCenter: selected,
              glBusinessArea: null,
              glAccount: null,
              glInternalOrder: null,
            }));
          }}
          disabled={isDisabled}
          placeholder={loading.profitCenter ? "Loading..." : "Select Profit Center"}
          isRequired={true}
        />
        <IconButton
          aria-label="Refresh Profit Center"
          icon={<RepeatIcon />}
          size="sm"
          onClick={() => fetchProfitCenters(true)}
          isLoading={loading.profitCenter}
          isDisabled={isDisabled || !companyCode?.value}
          mb="2px"
        />
      </HStack>

      <HStack alignItems="flex-end" spacing={1}>
        <CustomInput
          name="glBusinessArea"
          label="Business Area"
          type="select"
          isBoxSelect
          options={addNA(businessAreaOptions)}
          value={formData.glBusinessArea}
          onChange={(selected: any) =>
            setFormData((prev: any) => ({ ...prev, glBusinessArea: selected }))
          }
          disabled={isDisabled || !formData.glProfitCenter}
          isRequired={true}
        />
        <IconButton
          aria-label="Refresh Business Area"
          icon={<RepeatIcon />}
          size="sm"
          onClick={() => fetchBusinessArea(true)}
          isLoading={loading.businessArea}
          isDisabled={isDisabled || !formData.glProfitCenter}
          mb="2px"
        />
      </HStack>

      <HStack alignItems="flex-end" spacing={1}>
        <CustomInput
          name="glAccount"
          label="GL Account"
          type="select"
          isBoxSelect
          options={addNA(glAccountOptions)}
          value={formData.glAccount}
          onChange={(selected: any) => {
            setFormData((prev: any) => ({
              ...prev,
              glAccount: selected,
              glInternalOrder: null,
            }));
          }}
          disabled={isDisabled}
          isRequired={true}
        />
        <IconButton
          aria-label="Refresh GL Account"
          icon={<RepeatIcon />}
          size="sm"
          onClick={() => fetchGlAccounts(true)}
          isLoading={loading.glAccount}
          isDisabled={isDisabled || !companyCode?.value}
          mb="2px"
        />
      </HStack>

      <HStack alignItems="flex-end" spacing={1}>
        <CustomInput
          name="glInternalOrder"
          label="Internal Order"
          type="select"
          isBoxSelect
          options={addNA(internalOrderOptions)}
          value={formData.glInternalOrder}
          onChange={(selected: any) =>
            setFormData((prev: any) => ({ ...prev, glInternalOrder: selected }))
          }
          disabled={isDisabled}
          isRequired={true}
        />
        <IconButton
          aria-label="Refresh Internal Order"
          icon={<RepeatIcon />}
          size="sm"
          onClick={() => fetchInternalOrders(true)}
          isLoading={loading.internalOrder}
          isDisabled={isDisabled || !formData.glAccount}
          mb="2px"
        />
      </HStack>

      <CustomInput
        name="glAmount"
        label="Gl Amount"
        type="text"
        isBoxSelect
        placeholder="Gl Amount"
        value={formData.glAmount}
        onChange={(e: any) =>
          setFormData((prev: any) => ({ ...prev, glAmount: e.target.value }))
        }
        disabled={isDisabled}
        isRequired={true}
      />

      <CustomInput
        name="glLineItemsRemark"
        label="GL Line Item Remark"
        type="text"
        placeholder="Enter remark"
        value={formData.glLineItemsRemark || ""}
        onChange={(e: any) =>
          setFormData((prev: any) => ({
            ...prev,
            glLineItemsRemark: e.target.value,
          }))
        }
        disabled={isDisabled}
        isRequired={true}
      />

    </SimpleGrid>
  );
}
